/**
 * Role Detail Page - Shows role info + full PermissionMatrix
 * System roles: read-only. Custom roles: editable with Save/Cancel.
 */

import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { ArrowLeft, Shield, Users } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import {
  PermissionMatrix,
  PermissionValue,
  PermissionDefinition,
  AccessLevel,
} from '../../components/roles/PermissionMatrix';
import roleService, { Role, Permission } from '../../services/api/roleService';
import apiClient from '../../services/api/apiClient';
import { useAuthStore } from '../../stores/authStore';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';
import { RoleAccessEditor } from '../../components/roles/RoleAccessEditor';
import { IsPaidToggleGroup, IsPaidOption } from '../RoleManagementPage/CreateSystemRoleModal';

const toPermissionValues = (permissions: Permission[] | undefined): PermissionValue[] =>
  (permissions || [])
    .filter(
      (p): p is Permission & { accessLevel: NonNullable<Permission['accessLevel']> } =>
        !!p.accessLevel,
    )
    .map((p) => ({ permissionId: p.id, accessLevel: p.accessLevel }));

export const RoleDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const isSuperAdmin = useIsSuperAdmin();

  const [editPermissions, setEditPermissions] = useState<PermissionValue[] | null>(null);
  // System-template-only editable fields (super admin). Populated on entering edit mode.
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsPaid, setEditIsPaid] = useState(false);

  const { data: role, isLoading: roleLoading } = useQuery<Role>({
    queryKey: ['role', id],
    queryFn: () => roleService.getRoleById(id!),
    enabled: !!id,
  });

  const { data: allPermissions = [] } = useQuery<PermissionDefinition[]>({
    queryKey: ['all-permissions'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/permissions');
      const grouped = res.data as Record<string, PermissionDefinition[]>;
      return Object.values(grouped).flat();
    },
  });

  const isSystemTemplate = role?.isSystem && !role?.organizationId;

  const updateMutation = useMutation({
    mutationFn: (permissions: PermissionValue[]) => {
      const payload = {
        permissions: permissions.map((p) => ({
          permissionId: p.permissionId,
          accessLevel: p.accessLevel,
        })),
      };
      // Super admins use the system role endpoint for system templates, and can
      // also update the template's name, description, and paid-seat flag.
      if (isSystemTemplate) {
        return roleService.updateSystemRole(id!, {
          ...payload,
          name: editName.trim() || undefined,
          description: editDescription.trim() || undefined,
          isPaid: editIsPaid,
        });
      }
      return roleService.updateRole(id!, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role', id] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setEditPermissions(null);
    },
  });

  // Super admins can edit system templates; org admins can edit custom roles
  const canEdit = isSystemTemplate ? isSuperAdmin : hasPermission('role:extend');
  const isEditing = editPermissions !== null;
  // Org admins editing an org role get the simplified workflow + scope editor;
  // super admins (and system templates) get the full granular PermissionMatrix.
  const useWorkflowEditor = !isSuperAdmin && !isSystemTemplate;

  const currentValues = useMemo(() => toPermissionValues(role?.permissions), [role?.permissions]);

  // For custom roles based on a template, fetch base permissions for diff
  const { data: baseRole } = useQuery<Role>({
    queryKey: ['role', role?.basedOnId],
    queryFn: () => roleService.getRoleById(role!.basedOnId!),
    enabled: !!role?.basedOnId,
  });
  const baseValues = useMemo(
    () => (baseRole ? toPermissionValues(baseRole.permissions) : undefined),
    [baseRole],
  );

  /**
   * Ceiling map for derived roles: keyed by permission KEY (e.g. 'team:manage').
   * Permissions absent from the parent (parent OFF) are not included → locked OFF.
   * Passed to <PermissionMatrix ceiling={ceiling}> so the UI can enforce constraints.
   * Server remains source of truth; API 400 errors surface via the existing error toast.
   *
   * `baseRole` is only fetched when `role.basedOnId` is set (see useQuery enabled flag),
   * so the guard on `baseRole?.permissions` is sufficient — no need to also check basedOnId.
   * Dep is `baseRole` (the stable query result object) not `baseRole?.permissions` (unstable
   * array reference) so the memo only recomputes when the server data actually changes.
   */
  const ceiling = useMemo<Record<string, AccessLevel> | undefined>(() => {
    if (!baseRole?.permissions) return undefined;
    const map: Record<string, AccessLevel> = {};
    for (const p of baseRole.permissions) {
      if (p.accessLevel) {
        map[p.key] = p.accessLevel;
      }
    }
    return map;
  }, [baseRole]);

  const handleStartEdit = () => {
    setEditPermissions([...currentValues]);
    setEditName(role?.name ?? '');
    setEditDescription(role?.description ?? '');
    setEditIsPaid(role?.isPaid ?? false);
  };

  const handleCancel = () => {
    setEditPermissions(null);
  };

  const handleSave = () => {
    if (!editPermissions) return;
    // System templates require a name.
    if (isSystemTemplate && !editName.trim()) return;
    updateMutation.mutate(editPermissions);
  };

  if (roleLoading) {
    return (
      <Container>
        <EmptyState>Loading role...</EmptyState>
      </Container>
    );
  }

  if (!role) {
    return (
      <Container>
        <EmptyState>Role not found.</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <BackLink onClick={() => navigate('/roles')}>
        <ArrowLeft size={16} />
        Back to Roles
      </BackLink>

      <RoleHeader>
        <RoleIcon>
          <Shield size={24} />
        </RoleIcon>
        <RoleInfo>
          <RoleNameRow>
            <RoleName>{role.name}</RoleName>
            {isSystemTemplate && <Badge variant="neutral">System Role</Badge>}
            {role.basedOnName && <Badge variant="neutral">Based on: {role.basedOnName}</Badge>}
          </RoleNameRow>
          {role.description && <RoleDesc>{role.description}</RoleDesc>}
          <RoleMeta>
            <MetaItem>
              <Users size={14} />
              {role.userCount || 0} users
            </MetaItem>
            <MetaItem>{role.permissions?.length || 0} permissions</MetaItem>
          </RoleMeta>
        </RoleInfo>
        {canEdit && !isEditing && !useWorkflowEditor && (
          <Button variant="primary-blue" size="sm" onClick={handleStartEdit}>
            Edit Permissions
          </Button>
        )}
      </RoleHeader>

      {isEditing && isSystemTemplate && (
        <DetailsCard>
          <FieldGroup>
            <FieldLabel>Role name *</FieldLabel>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Description</FieldLabel>
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Briefly describe this role's purpose"
              rows={2}
            />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Paid role</FieldLabel>
            <IsPaidToggleGroup>
              <IsPaidOption
                $active={editIsPaid === false}
                onClick={() => setEditIsPaid(false)}
                type="button"
              >
                No — free seat
              </IsPaidOption>
              <IsPaidOption
                $active={editIsPaid === true}
                onClick={() => setEditIsPaid(true)}
                type="button"
              >
                Yes — paid seat
              </IsPaidOption>
            </IsPaidToggleGroup>
          </FieldGroup>
        </DetailsCard>
      )}

      {useWorkflowEditor ? (
        <RoleAccessEditor
          roleId={id!}
          roleKeys={(role.permissions ?? []).map((p) => p.key)}
          templateKeys={(baseRole?.permissions ?? []).map((p) => p.key)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['role', id] });
            queryClient.invalidateQueries({ queryKey: ['roles'] });
          }}
        />
      ) : (
        <>
          <PermissionsCard>
            <PermissionMatrix
              permissions={allPermissions}
              value={isEditing ? editPermissions : currentValues}
              onChange={isEditing ? setEditPermissions : () => {}}
              basePermissions={baseValues}
              readOnly={!isEditing}
              ceiling={ceiling}
            />
          </PermissionsCard>

          {isEditing && (
            <ActionBar>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="primary-blue"
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </ActionBar>
          )}
        </>
      )}
    </Container>
  );
};

export default RoleDetailPage;

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary.base};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

const RoleHeader = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[5]};
`;

const RoleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary.lightSky}20;
  color: ${({ theme }) => theme.colors.primary.ocean};
  flex-shrink: 0;
`;

const RoleInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const RoleNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
`;

const RoleName = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const RoleDesc = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const RoleMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
`;

const PermissionsCard = styled.div``;

const DetailsCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[5]};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  resize: vertical;
  font-family: inherit;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary.base}20;
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  position: sticky;
  bottom: ${({ theme }) => theme.spacing[4]};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
`;
